import React, { useState, useEffect } from 'react';

interface PDFInfo {
  formName: string;
  fileName: string;
  blobUrl: string;
  pageCount: number;
}

interface PDFSelectionModalProps {
  isOpen: boolean;
  generatedPDFs: PDFInfo[];
  onClose: () => void;
  onEditSelected: (selectedFormNames: string[]) => void;
}

export const PDFSelectionModal: React.FC<PDFSelectionModalProps> = ({
  isOpen,
  generatedPDFs,
  onClose,
  onEditSelected,
}) => {
  const [selectedPDFs, setSelectedPDFs] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedPDFs(new Set());
      setSelectAll(false);
    }
  }, [isOpen]);

  // Update select all state based on individual selections
  useEffect(() => {
    const allSelected = generatedPDFs.length > 0 && selectedPDFs.size === generatedPDFs.length;
    setSelectAll(allSelected);
  }, [selectedPDFs, generatedPDFs.length]);

  if (!isOpen) return null;

  const handleSelectAll = () => {
    if (selectAll) {
      // Unselect all
      setSelectedPDFs(new Set());
    } else {
      // Select all
      const allFormNames = new Set(generatedPDFs.map(pdf => pdf.formName));
      setSelectedPDFs(allFormNames);
    }
  };

  const handlePDFToggle = (formName: string) => {
    const newSelected = new Set(selectedPDFs);
    if (newSelected.has(formName)) {
      newSelected.delete(formName);
    } else {
      newSelected.add(formName);
    }
    setSelectedPDFs(newSelected);
  };

  const handleEditSelected = () => {
    const selectedFormNames = Array.from(selectedPDFs);
    onEditSelected(selectedFormNames);
  };

  const isEditDisabled = selectedPDFs.size === 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Select PDFs to Edit
          </h2>
          <p className="text-gray-600">
            Choose which PDFs you want to edit. Selected PDFs will be regenerated while maintaining their group association.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Select All Option */}
            <div className="flex items-center p-3 border rounded-lg bg-gray-50">
              <input
                type="checkbox"
                id="select-all"
                checked={selectAll}
                onChange={handleSelectAll}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
              />
              <label htmlFor="select-all" className="text-sm font-medium text-gray-900 flex-1">
                Select All PDFs ({generatedPDFs.length} total)
              </label>
            </div>

            {/* Individual PDF Selection */}
            <div className="space-y-2">
              {generatedPDFs.map((pdf) => (
                <div key={pdf.formName} className="flex items-center p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    id={`pdf-${pdf.formName.replace(/[^\w]/g, '-')}`}
                    checked={selectedPDFs.has(pdf.formName)}
                    onChange={() => handlePDFToggle(pdf.formName)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                  />
                  <label htmlFor={`pdf-${pdf.formName.replace(/[^\w]/g, '-')}`} className="text-sm text-gray-900 flex-1">
                    <div className="font-medium">{pdf.formName}</div>
                    <div className="text-gray-500 text-xs">{pdf.pageCount} pages</div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200">
          <div className="flex space-x-4">
            <button
              onClick={onClose}
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleEditSelected}
            disabled={isEditDisabled}
            className={`flex-1 font-medium py-3 px-6 rounded-lg transition-colors duration-200 ${
              isEditDisabled
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            Edit Selected ({selectedPDFs.size})
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};